import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { Modal } from "components/Modal/Modal";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import SimpleNotification from "components/Notification/SimpleNotification";
import { useCallback, useState } from "react";
import {
    TriggerKind,
    TriggerStampResult,
    formatSchema,
    triggerStampEndpoint,
} from "./triggerInputSchemaStamp.helpers";
// Reuses the two-pane current/new diff styling shared with the hook stamp modal.
import "./HookInputSchemaStamp.scss";
import "./TriggerStampButton.scss";

interface TriggerStampButtonProps {
    /** Which trigger record this button stamps ("webhook"). */
    triggerKind: TriggerKind;
    /** The saved trigger record id (stamping operates on the saved record). */
    triggerId?: string;
    /** Enable the button only when a flow is bound to the trigger. */
    flowSelected: boolean;
    /** Follow the field's read-only/disabled state. */
    disabled?: boolean;
    /** Called after a successful stamp so the caller can refetch the record. */
    fetchResourceData?: (() => any) | null;
}

/**
 * The one-click "Stamp input schema" button that sits next to the flow picker on
 * the webhook record screen. Clicking runs a dry run (`apply=false`);
 * if the canonical trigger-envelope input schema would change the bound flow's
 * current one it opens a confirm dialog showing current-vs-new, and only then
 * writes it (`apply=true`). Mirrors the object-template HookStampButton.
 * See operator-task-fe-stamp-input-schema-webhook-schedule-action.
 */
export const TriggerStampButton = ({
    triggerKind,
    triggerId,
    flowSelected,
    disabled,
    fetchResourceData,
}: TriggerStampButtonProps) => {
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState<TriggerStampResult | null>(null);
    const [isApplying, setIsApplying] = useState(false);

    const handleStampClick = useCallback(() => {
        if (!triggerId) {
            return;
        }
        setLoading(true);
        restClient
            .post(triggerStampEndpoint(triggerKind, triggerId), { apply: false })
            .then((response) => {
                const result: TriggerStampResult = response?.data;
                if (!result?.changed) {
                    NotificationService.info(
                        <SimpleNotification
                            title="Input schema already up to date"
                            subtitle={`The ${triggerKind}'s target flow already has the correct input schema.`}
                        />
                    );
                    return;
                }
                setConfirm(result);
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride={`Could not compute the ${triggerKind} input schema`}
                        error={error}
                    />
                );
            })
            .finally(() => setLoading(false));
    }, [triggerKind, triggerId]);

    const handleConfirmApply = useCallback(() => {
        if (!triggerId || !confirm) {
            return;
        }
        setIsApplying(true);
        restClient
            .post(triggerStampEndpoint(triggerKind, triggerId), { apply: true })
            .then(() => {
                NotificationService.success(
                    <SimpleNotification
                        title="Input schema stamped"
                        subtitle={`The correct input schema was written onto flow "${confirm.flow_name}".`}
                    />
                );
                setConfirm(null);
                fetchResourceData?.();
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride={`Could not stamp the ${triggerKind} input schema`}
                        error={error}
                    />
                );
            })
            .finally(() => setIsApplying(false));
    }, [triggerKind, triggerId, confirm, fetchResourceData]);

    return (
        <>
            <div className="triggerStampButton">
                <Button
                    size="small"
                    variation="dark"
                    label="Stamp input schema"
                    onClick={handleStampClick}
                    loading={loading}
                    disabled={!flowSelected || !!disabled || loading}
                />
            </div>
            <Modal
                isVisible={!!confirm}
                onCancel={() => (isApplying ? undefined : setConfirm(null))}
                title="Overwrite the flow input schema?"
                width="640px"
                closable={false}
                destroyOnClose
            >
                {confirm && (
                    <div className="hookInputSchemaStamp__confirm">
                        <p className="hookInputSchemaStamp__confirmText">
                            This overwrites the current input schema of the flow{" "}
                            <strong>{confirm.flow_name}</strong> with the canonical schema for this{" "}
                            {triggerKind}'s trigger envelope. You can still edit it afterwards.
                        </p>
                        <div className="hookInputSchemaStamp__diff">
                            <div className="hookInputSchemaStamp__diffPane">
                                <div className="hookInputSchemaStamp__diffLabel">Current</div>
                                <pre className="hookInputSchemaStamp__code">
                                    {formatSchema(confirm.current_input_schema)}
                                </pre>
                            </div>
                            <div className="hookInputSchemaStamp__diffPane">
                                <div className="hookInputSchemaStamp__diffLabel">New</div>
                                <pre className="hookInputSchemaStamp__code">
                                    {formatSchema(confirm.computed_input_schema)}
                                </pre>
                            </div>
                        </div>
                        {/*
                          Canonical modal button row (ui-design-principle-modals-dialogs
                          reference impl): Cancel on the LEFT as the neutral white button,
                          primary-blue confirm on the RIGHT. No corner close "×".
                        */}
                        <div className="hookInputSchemaStamp__actions">
                            <Button
                                label="Cancel"
                                variation="white"
                                onClick={() => setConfirm(null)}
                                disabled={isApplying}
                            />
                            <Button
                                label="Stamp input schema"
                                variation="primary"
                                onClick={handleConfirmApply}
                                loading={isApplying}
                                disabled={isApplying}
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};
